
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Award, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface SkillTestAttempt {
  id: string;
  skill: string;
  created_at: string;
}

interface SkillTestsCardProps {
  skills: string[];
  quizAttempts: SkillTestAttempt[];
  onStartQuiz: (skill: string) => void;
}

const SkillTestsCard: React.FC<SkillTestsCardProps> = ({ skills, quizAttempts, onStartQuiz }) => {
  const { toast } = useToast();

  const handleStartQuiz = (skill: string) => {
    const attempt = quizAttempts.find(a => a.skill === skill);
    if (attempt) {
      const attemptDate = new Date(attempt.created_at);
      const currentDate = new Date();
      const diffTime = Math.abs(Number(currentDate) - Number(attemptDate));
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 7) {
        toast({
          title: "Quiz attempt limit reached",
          description: `You can retake this quiz after ${7 - diffDays} days.`,
          variant: "destructive",
        });
        return;
      }
    }

    onStartQuiz(skill);
  };

  return (
    <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80">
      <CardHeader>
        <CardTitle className="text-xl">Skill Tests</CardTitle>
        <CardDescription>
          Take skill tests to showcase your abilities
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {skills.map((skill) => {
            const attempted = quizAttempts.find(a => a.skill === skill);
            const isDisabled = attempted && ((new Date().getTime() - new Date(attempted.created_at).getTime()) / (1000 * 60 * 60 * 24) < 7);
            
            return (
              <div key={skill} className="flex justify-between items-center border-b border-[#0EA5E9]/10 pb-2">
                <div className="flex items-center gap-2">
                  <Award className="h-4 w-4 text-[#0EA5E9]" />
                  <span>{skill}</span>
                </div>
                <div className="flex items-center gap-2">
                  {attempted && (
                    <span className="text-xs bg-[#0EA5E9]/20 text-[#0EA5E9] px-2 py-1 rounded-full flex items-center gap-1">
                      <Check className="h-3 w-3" /> Completed
                    </span>
                  )}
                  <Button 
                    size="sm" 
                    variant={isDisabled ? "outline" : "default"}
                    onClick={() => handleStartQuiz(skill)}
                    disabled={isDisabled}
                    className={isDisabled ? "text-gray-400 border-gray-300" : "bg-[#0EA5E9] hover:bg-[#0EA5E9]/90 text-white"}
                  >
                    {attempted ? 'Retake' : 'Start'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default SkillTestsCard;
